import { useCallback, useEffect, useState } from "react";
import { Plus, Upload } from "lucide-react";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { AgentsRegistry } from "../components/AgentsRegistry";
import type { InsurerCompany, NetworkHospital } from "../api/insurerApi";
import {
  createClaimsProvider,
  fetchClaimsCompanies,
  fetchClaimsNetwork,
  fetchCoverageHistory,
  importCoverageList,
  type CoverageImport,
} from "../api/claimsApi";

const buttonClass =
  "inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60";

const PROVIDER_TYPES = [
  { value: "hopital", label: "Hôpital" },
  { value: "clinique", label: "Clinique" },
  { value: "pharmacie", label: "Pharmacie" },
  { value: "laboratoire", label: "Laboratoire" },
  { value: "centre_specialise", label: "Centre spécialisé" },
  { value: "cabinet_medical", label: "Cabinet médical" },
];

/**
 * Compte Sinistre : référentiels de l'assureur actif (choisi dans l'en-tête) —
 * entreprises clientes, liste des agents couverts, réseau de prestataires.
 */
export function ClaimsPortfolioPage() {
  const { showToast } = useToast();
  const [companies, setCompanies] = useState<InsurerCompany[]>([]);
  const [network, setNetwork] = useState<NetworkHospital[]>([]);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [history, setHistory] = useState<CoverageImport[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [coverageFile, setCoverageFile] = useState<File | null>(null);
  const [deactivateMissing, setDeactivateMissing] = useState(false);
  const [agentsRefresh, setAgentsRefresh] = useState(0);
  const [providerName, setProviderName] = useState("");
  const [providerType, setProviderType] = useState("hopital");
  const [providerCity, setProviderCity] = useState("");

  const load = useCallback(async () => {
    try {
      setError(null);
      const [companyRows, networkRows] = await Promise.all([fetchClaimsCompanies(), fetchClaimsNetwork()]);
      setCompanies(companyRows);
      setNetwork(networkRows);
      setCompanyId((current) => current ?? companyRows[0]?.id ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Chargement impossible.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!companyId) return;
    fetchCoverageHistory(companyId)
      .then(setHistory)
      .catch(() => setHistory([]));
  }, [companyId]);

  const run = async (action: () => Promise<void>, success: string) => {
    setError(null);
    setSaving(true);
    try {
      await action();
      showToast(success);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  const handleImport = () =>
    run(async () => {
      if (!companyId || !coverageFile) return;
      const result = await importCoverageList(companyId, coverageFile, deactivateMissing);
      setCoverageFile(null);
      setHistory(await fetchCoverageHistory(companyId));
      setAgentsRefresh((n) => n + 1);
      if (result.errors.length > 0) {
        setError(`${result.errors.length} ligne(s) ignorée(s) : ${result.errors.slice(0, 3).join(" ")}`);
      }
    }, "Liste des agents et ayants droit importée.");

  return (
    <>
      <Breadcrumbs items={[{ label: "Référentiels de l'assureur" }]} />
      {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className="theme-card flex flex-col gap-5 p-5">
          <h1 className="text-lg font-semibold text-foreground">Entreprises clientes ({companies.length})</h1>

          <ul className="flex flex-col gap-2">
            {companies.length === 0 ? <li className="text-sm text-muted">Aucune entreprise pour cet assureur.</li> : null}
            {companies.map((company) => (
              <li key={company.id}>
                <button
                  type="button"
                  onClick={() => setCompanyId(company.id)}
                  className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                    company.id === companyId ? "border-primary bg-primary/5" : "border-border hover:bg-accent/20 hover:border-accent"
                  }`}
                >
                  {company.name}
                </button>
              </li>
            ))}
          </ul>

          <p className="text-xs text-muted">Consultation : les entreprises sont créées et rattachées à l'assureur par l'Admin.</p>

          {companyId ? (
            <div className="flex flex-col gap-3 border-t border-border pt-4">
              <h2 className="text-sm font-semibold text-foreground">Liste des agents couverts</h2>
              <p className="text-xs text-muted">
                Fichier CSV envoyé par l'assureur, avec l'en-tête : matricule ; nom ; date_naissance ; sexe ; telephone ; lien.
                La colonne « lien » est facultative : vide ou « agent » pour l'agent ; « conjoint », « enfant » ou « parent »
                pour un ayant droit, avec le matricule de l'agent dont il dépend.
              </p>
              <input
                id="coverage-file"
                type="file"
                accept=".csv,text/csv"
                className="text-sm"
                onChange={(event) => setCoverageFile(event.target.files?.[0] ?? null)}
              />
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  id="coverage-deactivate"
                  type="checkbox"
                  checked={deactivateMissing}
                  onChange={(event) => setDeactivateMissing(event.target.checked)}
                />
                Désactiver les agents absents de cette liste
              </label>
              <button
                type="button"
                className={`${buttonClass} self-start`}
                disabled={saving || !coverageFile}
                onClick={() => void handleImport()}
              >
                <Upload size={15} />
                Importer la liste
              </button>

              {history.length > 0 ? (
                <ul className="flex flex-col gap-1 text-xs text-muted">
                  {history.map((item) => (
                    <li key={item.id}>
                      {item.imported_at} · {item.file_name} · {item.created} créés, {item.updated} mis à jour,{" "}
                      {item.deactivated} désactivés{item.errors.length ? `, ${item.errors.length} erreur(s)` : ""}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </section>

        <section className="theme-card flex flex-col gap-5 p-5">
          <h2 className="text-lg font-semibold text-foreground">Réseau de prestataires ({network.length})</h2>
          <p className="text-xs text-muted">Seul l'Admin peut retirer un prestataire du réseau.</p>
          <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {network.length === 0 ? <li className="px-3 py-3 text-sm text-muted">Aucun prestataire dans le réseau.</li> : null}
            {network.map((hospital) => (
              <li key={hospital.hospital_id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span className="min-w-0">
                  <span className="block truncate font-medium text-foreground">{hospital.name}</span>
                  <span className="block text-xs text-muted">{[hospital.type, hospital.city].filter(Boolean).join(" · ") || "—"}</span>
                </span>
              </li>
            ))}
          </ul>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <FormField label="Nouveau prestataire">
              <input id="provider-name" value={providerName} onChange={(event) => setProviderName(event.target.value)} className={inputClass} />
            </FormField>
            <FormField label="Type">
              <select id="provider-type" value={providerType} onChange={(event) => setProviderType(event.target.value)} className={selectClass}>
                {PROVIDER_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Ville">
              <input id="provider-city" value={providerCity} onChange={(event) => setProviderCity(event.target.value)} className={inputClass} />
            </FormField>
          </div>
          <button
            type="button"
            className={`${buttonClass} self-start`}
            disabled={saving || !providerName.trim()}
            onClick={() =>
              void run(async () => {
                await createClaimsProvider({ commercial_name: providerName.trim(), type: providerType, city: providerCity.trim() });
                setProviderName("");
                setProviderCity("");
                await load();
              }, "Prestataire enregistré et ajouté au réseau.")
            }
          >
            <Plus size={15} />
            Enregistrer le prestataire
          </button>
        </section>
      </div>

      {companyId ? (
        <div className="mt-6">
          <AgentsRegistry companyId={companyId} refreshKey={agentsRefresh} />
        </div>
      ) : null}
    </>
  );
}
